import Cookies from 'universal-cookie';

const {
  NODE_ENV,
  BASE_URI,
  GOOGLE_CLIENT_ID,
  GITHUB_CLIENT_ID,
  OAUTH_REDIRECT_ORIGIN,
  CLIENT_DOMAIN_PATTERN,
} = process.env;

const cookies = new Cookies();

const STATE_STORAGE_KEY = 'oauth_state';
// only needs to survive the round trip to the provider and back
const STATE_COOKIE_MAX_AGE = 600;
// browsers cap a cookie's value around 4KB and providers cap the `state` size
const MAX_RETURN_QUERY_LENGTH = 1024;

const PROVIDERS = {
  google: {
    authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    clientId: GOOGLE_CLIENT_ID,
    scope: 'openid email profile',
  },
  github: {
    authorizeUrl: 'https://github.com/login/oauth/authorize',
    clientId: GITHUB_CLIENT_ID,
    scope: 'read:user user:email',
  },
};

// unique random value to identify session
function generateNonce() {
  if (window.crypto && window.crypto.getRandomValues) {
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
      '',
    );
  }
  return `${Math.random().toString(36).slice(2)}${Date.now()}`;
}

// Extracts the store's client_id from the current subdomain
function getSwellClientId() {
  if (!CLIENT_DOMAIN_PATTERN) {
    return undefined;
  }
  const match = window.location.hostname.match(
    new RegExp(CLIENT_DOMAIN_PATTERN),
  );
  return match ? match[1] : undefined;
}

// The OAuth redirect can land on a different subdomain than the one we started from
function getCookieDomain() {
  const { hostname } = window.location;
  if (!hostname || hostname === 'localhost') {
    return undefined;
  }
  return `.${hostname.split('.').slice(-2).join('.')}`;
}

function oauthStateCookieOptions() {
  return {
    path: '/',
    maxAge: STATE_COOKIE_MAX_AGE,
    domain: getCookieDomain(),
    // client cookies cannot be http only
    httpOnly: false,
    sameSite: 'lax',
    secure: NODE_ENV !== 'development',
  };
}

function getRedirectUri(provider) {
  // redirect url should exactly match registered url in the provider application
  const origin = OAUTH_REDIRECT_ORIGIN || window.location.origin;
  return `${origin}${BASE_URI}/oauth/${provider}/callback`;
}

function encodeBase64(value) {
  try {
    return Buffer.from(value, 'utf-8').toString('base64');
  } catch (err) {
    return '';
  }
}

export function decodeBase64(value) {
  try {
    const decoded = Buffer.from(value, 'base64').toString('utf-8');
    // function can return garbage values for non base64 strings. We should check result
    return encodeBase64(decoded) === value ? decoded : undefined;
  } catch (err) {
    return undefined;
  }
}

function buildAuthorizeUrl(provider, state) {
  const config = PROVIDERS[provider];
  if (!config) {
    throw new Error(`Unsupported OAuth provider: ${provider}`);
  }

  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: getRedirectUri(provider),
    scope: config.scope,
    state,
    response_type: 'code',
  });

  return `${config.authorizeUrl}?${params.toString()}`;
}

const oauth = {
  // Redirects the browser to the provider's consent screen
  //  flow is 'login' or 'signup' and is only used client-side to decide where to redirect user
  //  swellClientId is clientId initialized oath session
  //  swellPartnerId is the partner id initialized oath session (partner dashboard only,
  //  taken from the `:partnerId` route param since it isn't derivable from the subdomain)
  //  cliUrl is the CLI's callback url (read from the `cliUrl` cookie) when a CLI login is in
  //  progress, so the callback can send the CLI its session instead of a normal login
  // current state is stored into a cookie and is restored after we are redirected to the target store
  startOAuth(provider, flow, partnerId) {
    const nonce = generateNonce();
    const swellClientId = getSwellClientId() || '';
    const swellPartnerId = partnerId || '';
    // a partner-dashboard login can never also be a CLI login; ignore any
    // leftover `cliUrl` cookie from an abandoned CLI flow in that case
    const cliUrl = swellPartnerId ? '' : cookies.get('cliUrl') || '';
    // preserve the current page's query params (e.g. ?to=/orders) so they can be restored;
    // drop it rather than risk oversizing the cookie/state param and corrupting the whole
    // oauth state (nonce included) if the redirect target happens to be unusually large
    const rawReturnQuery = window.location.search.replace(/^\?/, '');
    const returnQuery =
      rawReturnQuery.length <= MAX_RETURN_QUERY_LENGTH ? rawReturnQuery : '';
    cookies.set(
      STATE_STORAGE_KEY,
      JSON.stringify({
        nonce,
        flow,
        swellClientId,
        swellPartnerId,
        cliUrl,
        returnQuery,
      }),
      oauthStateCookieOptions(),
    );

    window.location.href = buildAuthorizeUrl(
      provider,
      [
        nonce,
        flow,
        swellClientId,
        swellPartnerId,
        cliUrl ? encodeBase64(cliUrl) : '',
        returnQuery ? encodeBase64(returnQuery) : '',
      ].join('|'),
    );
  },

  // Verifies the `state` returned by the provider against the nonce stored before redirecting.
  // A missing or mismatched stored nonce is always treated as invalid state
  consumeOAuthState(returnedState) {
    const [
      nonce,
      embeddedFlow,
      embeddedSwellClientId,
      embeddedSwellPartnerId,
      embeddedCliUrl,
      embeddedReturnQuery,
    ] = String(returnedState || '').split('|');

    const raw = cookies.get(STATE_STORAGE_KEY, { doNotParse: true });
    cookies.remove(STATE_STORAGE_KEY, oauthStateCookieOptions());

    const INVALID_STATE = {
      flow: null,
      swellClientId: undefined,
      swellPartnerId: undefined,
      cliUrl: undefined,
      returnQuery: undefined,
    };

    if (!raw) {
      // allow local development, we redirect to localhost that cannot get saved cookie from swell.test
      if (window.location.hostname === 'localhost' && embeddedFlow) {
        const decodedCliUrl = embeddedCliUrl
          ? decodeBase64(embeddedCliUrl)
          : undefined;
        const decodedReturnQuery = embeddedReturnQuery
          ? decodeBase64(embeddedReturnQuery)
          : undefined;
        return {
          flow: embeddedFlow,
          swellClientId: embeddedSwellClientId || undefined,
          swellPartnerId: embeddedSwellPartnerId || undefined,
          cliUrl: decodedCliUrl,
          returnQuery: decodedReturnQuery,
        };
      }
      return INVALID_STATE;
    }

    try {
      const stored = JSON.parse(raw);
      return stored.nonce && stored.nonce === nonce
        ? {
            flow: stored.flow,
            swellClientId: stored.swellClientId || undefined,
            swellPartnerId: stored.swellPartnerId || undefined,
            cliUrl: stored.cliUrl || undefined,
            returnQuery: stored.returnQuery || undefined,
          }
        : INVALID_STATE;
    } catch (err) {
      return INVALID_STATE;
    }
  },
};

export default oauth;
